import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, TextAreaInput } from '@aqarmap/aqarmap-ui';
import {
  useGetUserQuery,
  useUpdateCompanyMutation,
} from '~/redux/api/userApiSlice';
import useFormValidations from '~/hooks/useFormValidations';
import ErrorMessage from '~/components/ErrorMessage';
import AddNewImage from '~/components/AddNewImage';
import ChangeImage from '~/components/ChangeImage';
import { UpdatePhotos } from '~myAccount/enums';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import { objectSerialize } from '~/lib';
import PageLayout from '~myAccount/components/PageLayout';
import { useFullRoutes } from '~/hooks';

const CompanyUpdate = () => {
  const navigate = useNavigate();
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const { t } = useTranslation('app');
  const {
    data: response,
    isLoading: userIsLoading,
    isSuccess: userIsSuccess,
  } = useGetUserQuery();
  const companyLogo = response?.user.logo?.thumbnails.large as string;
  const isSubAccount = response?.user?.isSubAccount;
  const Bio = response?.user.bio;
  const { descriptionSchema } = useFormValidations();
  const [
    updateCompany,
    {
      isLoading: updateUserIsLoading,
      isError: updateUserIsError,
      error: updateUserError,
    },
  ] = useUpdateCompanyMutation();

  const updateDescriptionSchema = z.object({ description: descriptionSchema });

  type IUpdateCompanyDescription = z.infer<typeof updateDescriptionSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IUpdateCompanyDescription>({
    resolver: zodResolver(updateDescriptionSchema),
    mode: 'onChange',
    values: { description: Bio ?? '' },
  });
  const onSubmit = async (data: IUpdateCompanyDescription) => {
    const formData = objectSerialize({
      'user_update_company_info[logo][file]': imageFile as File,
      'user_update_company_info[bio]': data.description,
    });
    const res: any = await updateCompany(formData);

    if (res.data && !res.error) {
      navigate(MY_ACCOUNT_URL.COMPANY_INFO);
    }
  };
  const errorMessage = (updateUserError as any)?.data?.message;

  useEffect(() => {
    const pathname = location.pathname;
    const updateCompanyUrl = MY_ACCOUNT_URL.UPDATE_COMPANY;

    if (pathname === updateCompanyUrl && isSubAccount) {
      navigate(MY_ACCOUNT_URL.COMPANY_INFO);
    }
  }, [isSubAccount]);
  const companyLogoImg = response?.user.logo?.thumbnails.large;

  return (
    <PageLayout
      title={t('companyInfo')}
      bodyClassName="border border-gray rounded-[8px]"
    >
      {userIsLoading && <LayoutSkeleton />}
      {updateUserIsError && <ErrorMessage message={errorMessage} />}

      {!userIsLoading && userIsSuccess && (
        <div className='className="border border-gray rounded-[8px]'>
          <div className="p-3x">
            {companyLogo ? (
              <ChangeImage
                title={t('companyLogo')}
                type={UpdatePhotos.LOGO}
                imgSrc={companyLogo}
                getImageFile={(file) => setImageFile(file)}
              />
            ) : (
              <AddNewImage
                title={t('companyLogo')}
                getImageFile={(file) => setImageFile(file)}
              />
            )}
            {companyLogoImg && !response.user.is_valid_logo && (
              <p className="text-body_2 mt-x w-full bg-orange_light_2 text-orange_dark_2 font-normal p-1.5x rounded-[8px] mb-3x">
                {t('app:logoStillProcessing')}
              </p>
            )}
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextAreaInput
                  field={{
                    placeholder: t('companyDescriptionPlaceholder'),
                    autoFocus: true,
                    className: 'h-[100px]',
                    ...field,
                  }}
                  error={errors['description']?.message}
                  label={{
                    text: t('companyDescription'),
                    className: 'font-[600] block mt-3x',
                  }}
                  helperText={
                    t('maxCompanyDescription', { number: 512 }) as string
                  }
                />
              )}
            />
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center px-3x pb-3x gap-2x border-t border-gray pt-2x">
              <Button
                size="medium"
                ariaLabel="submit-btn"
                type="submit"
                variant="primary"
                isLoading={updateUserIsLoading}
                disabled={updateUserIsLoading}
                className="w-auto"
              >
                {t('save')}
              </Button>

              <Button
                size="medium"
                ariaLabel="cancel-btn"
                type="button"
                variant="secondary"
                className="w-auto"
                onClick={() => navigate(-1)}
              >
                {t('cancel')}
              </Button>
            </div>
          </form>
        </div>
      )}
    </PageLayout>
  );
};

export default CompanyUpdate;
